import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Edit, Monitor, Key, Network, Box, AlertTriangle, Link2, Unlink, ExternalLink, Plus } from 'lucide-react';
import api from '../lib/api';
import { parseApiDate } from '../lib/utils';
import toast from 'react-hot-toast';
import AttachmentsPanel from '../components/shared/Attachments';

const STATUS_CONFIG = {
  active:      { label: 'Active',      color: '#10b981', bg: '#d1fae5' },
  inactive:    { label: 'Inactive',    color: '#6b7280', bg: '#f3f4f6' },
  in_repair:   { label: 'In Repair',   color: '#f59e0b', bg: '#fef3c7' },
  retired:     { label: 'Retired',     color: '#9b9b98', bg: '#f3f4f6' },
  lost_stolen: { label: 'Lost/Stolen', color: '#ef4444', bg: '#fee2e2' },
};
const TYPE_ICONS = { monitor: Monitor, key: Key, network: Network, box: Box };

function Field({ label, value }) {
  if (!value && value !== 0) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13 }}>{value}</div>
    </div>
  );
}

function ExpiryField({ label, date }) {
  if (!date) return null;
  const d = new Date(date);
  const now = new Date();
  const daysLeft = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  const expired = daysLeft <= 0;
  const soon = daysLeft > 0 && daysLeft <= 30;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
        {d.toLocaleDateString()}
        {(expired || soon) && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: expired ? '#fee2e2' : '#fef3c7', color: expired ? '#ef4444' : '#d97706', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            <AlertTriangle size={9} /> {expired ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d left`}
          </span>
        )}
      </div>
    </div>
  );
}

export default function AssetDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [asset, setAsset]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [allTickets, setAllTickets] = useState([]);
  const [linkTicketId, setLinkTicketId] = useState('');
  const [showLinkForm, setShowLinkForm] = useState(false);

  const load = useCallback(() => {
    api.get(`/assets/${id}`).then(d => {
      setAsset(d.asset);
      setTickets(d.asset.tickets || []);
    }).catch(e => { toast.error(e.message); navigate('/assets'); }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/tickets?limit=200').then(d => setAllTickets(d.tickets || [])).catch(() => {});
  }, []);

  async function linkTicket() {
    if (!linkTicketId) return;
    try {
      await api.post(`/assets/${id}/tickets`, { ticket_id: linkTicketId });
      toast.success('Ticket linked');
      setLinkTicketId('');
      setShowLinkForm(false);
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function unlinkTicket(ticketId) {
    try {
      await api.delete(`/assets/${id}/tickets/${ticketId}`);
      toast.success('Ticket unlinked');
      load();
    } catch (e) { toast.error(e.message); }
  }

  if (loading) return <div className="page-container"><div className="text-muted">Loading...</div></div>;
  if (!asset) return null;

  const Icon = TYPE_ICONS[asset.type_icon] || Box;
  const statusCfg = STATUS_CONFIG[asset.status] || STATUS_CONFIG.active;

  // Parse custom fields from type
  let customFields = [];
  try {
    if (asset.type_id) {
      // We'll just show whatever custom_values exist
    }
  } catch {}

  const customEntries = Object.entries(asset.custom_values || {}).filter(([, v]) => v !== '' && v !== null);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: asset.type_color ? `${asset.type_color}20` : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Icon size={20} style={{ color: asset.type_color || '#6b7280' }} />
          </div>
          <div>
            <h1 className="page-title" style={{ marginBottom: 4 }}>{asset.name}</h1>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {asset.asset_tag && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>#{asset.asset_tag}</span>}
              <span style={{ padding: '2px 8px', borderRadius: 4, background: statusCfg.bg, color: statusCfg.color, fontSize: 11, fontWeight: 600 }}>{statusCfg.label}</span>
              {asset.type_name && <span style={{ padding: '2px 8px', borderRadius: 4, background: asset.type_color ? `${asset.type_color}20` : '#f3f4f6', color: asset.type_color || '#6b7280', fontSize: 11, fontWeight: 600 }}>{asset.type_name}</span>}
            </div>
          </div>
        </div>
        <button className="btn btn-secondary" onClick={() => navigate(`/assets/${id}/edit`)}><Edit size={14} /> Edit</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 20, alignItems: 'start' }}>
        {/* Main */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Details */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Details</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
              <Field label="Manufacturer"  value={asset.manufacturer} />
              <Field label="Model"         value={asset.model} />
              <Field label="Serial Number" value={asset.serial_number} />
              <Field label="Location"      value={asset.location} />
              <Field label="Organization"  value={asset.org_name} />
              <Field label="Purchase Date" value={asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString() : null} />
              <Field label="Purchase Price" value={asset.purchase_price ? `$${parseFloat(asset.purchase_price).toFixed(2)}` : null} />
            </div>
            <ExpiryField label="Warranty Expiry" date={asset.warranty_expiry} />
            <ExpiryField label="License Expiry"  date={asset.license_expiry}  />
            {asset.notes && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 6 }}>Notes</div>
                <div style={{ fontSize: 13, whiteSpace: 'pre-wrap' }}>{asset.notes}</div>
              </div>
            )}
          </div>

          {/* Custom fields */}
          {customEntries.length > 0 && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
              <h3 style={{ margin: '0 0 16px', fontSize: 14, fontWeight: 600 }}>Custom Fields</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
                {customEntries.map(([k, v]) => <Field key={k} label={k} value={v} />)}
              </div>
            </div>
          )}

          {/* Linked tickets */}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 20 }}>
            <div className="flex justify-between items-center" style={{ marginBottom: 14 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>Linked Tickets</h3>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkForm(s => !s)}><Plus size={13} /> Link Ticket</button>
            </div>

            {showLinkForm && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <select className="form-select" style={{ flex: 1 }} value={linkTicketId} onChange={e => setLinkTicketId(e.target.value)}>
                  <option value="">Select a ticket...</option>
                  {allTickets.filter(t => !tickets.find(lt => lt.id === t.id)).map(t => (
                    <option key={t.id} value={t.id}>#{t.ticket_number} — {t.subject?.slice(0,60)}</option>
                  ))}
                </select>
                <button className="btn btn-primary btn-sm" onClick={linkTicket} disabled={!linkTicketId}>Link</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setShowLinkForm(false)}>Cancel</button>
              </div>
            )}

            {tickets.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>No tickets linked to this asset.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tickets.map(t => (
                  <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 6, background: 'var(--bg-subtle)' }}>
                    <span style={{ padding: '1px 6px', borderRadius: 3, background: t.status_color ? `${t.status_color}20` : '#f3f4f6', color: t.status_color || '#6b7280', fontSize: 10, fontWeight: 600 }}>{t.status_name}</span>
                    <button onClick={() => navigate(`/tickets/${t.ticket_number}`)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--primary)', fontWeight: 500, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <ExternalLink size={11} /> #{t.ticket_number} — {t.subject?.slice(0,50)}
                    </button>
                    <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto', padding: '2px 6px', color: 'var(--text-muted)' }} onClick={() => unlinkTicket(t.id)} title="Unlink">
                      <Unlink size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="sidebar-section">
            <div className="sidebar-section-title">Asset Info</div>
            <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>Added</span>
                <span>{parseApiDate(asset.created_at).toLocaleDateString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>Added by</span>
                <span>{asset.created_by_name || '—'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--text-muted)' }}>Expiry alerts</span>
                <span style={{ color: asset.alert_on_expiry ? '#10b981' : '#9b9b98' }}>{asset.alert_on_expiry ? 'Enabled' : 'Disabled'}</span>
              </div>
            </div>
          </div>

          {/* Attachments */}
          {asset?.id && <AttachmentsPanel parentType="asset" parentId={asset.id} />}
        </div>
      </div>
    </div>
  );
}
